import type { DatasetRecord } from "../contracts/types";
import { parseCsv } from "./csv";
import { LIMITS } from "../domain/limits";

export interface FileLoadResult {
  records: DatasetRecord[];
  sourceFormat: "json" | "csv";
}

export class FileLoadError extends Error {}

function isJsonFile(file: File): boolean {
  return file.name.toLowerCase().endsWith(".json") || file.type === "application/json";
}

function isCsvFile(file: File): boolean {
  return file.name.toLowerCase().endsWith(".csv") || file.type === "text/csv";
}

/**
 * Lee el contenido de texto de un archivo con `FileReader` en vez de
 * `Blob.text()`: es la API clásica del File API (más ampliamente soportada,
 * incluida en entornos de prueba) y sigue sin tocar la red — todo ocurre en
 * memoria del navegador.
 */
function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(reader.error ?? new FileLoadError("No se pudo leer el archivo."));
    reader.readAsText(file);
  });
}

/**
 * Lee un archivo local (File API, 05-arquitectura-tecnica.md) y lo convierte
 * en filas de dataset. Todo ocurre en memoria del navegador: el archivo
 * nunca se sube a ningún servidor. Rechaza archivos por encima de
 * `LIMITS.maxUploadBytes` antes de leerlos, para no bloquear el hilo
 * principal con un archivo desproporcionado.
 */
export async function loadDatasetFile(file: File): Promise<FileLoadResult> {
  if (file.size > LIMITS.maxUploadBytes) {
    throw new FileLoadError(
      `El archivo pesa ${(file.size / (1024 * 1024)).toFixed(1)} MB, por encima del límite de ${LIMITS.maxUploadBytes / (1024 * 1024)} MB soportado en el navegador.`,
    );
  }
  if (!isJsonFile(file) && !isCsvFile(file)) {
    throw new FileLoadError("Formato no soportado: usa un archivo .json o .csv.");
  }

  const text = await readFileAsText(file);

  if (isCsvFile(file)) {
    return { records: parseCsv(text), sourceFormat: "csv" };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    throw new FileLoadError(`JSON inválido: ${error instanceof Error ? error.message : "no se pudo interpretar."}`);
  }
  if (Array.isArray(parsed)) {
    return { records: parsed as DatasetRecord[], sourceFormat: "json" };
  }
  if (parsed && typeof parsed === "object" && Array.isArray((parsed as { records?: unknown }).records)) {
    return { records: (parsed as { records: DatasetRecord[] }).records, sourceFormat: "json" };
  }
  throw new FileLoadError('El JSON debe ser un array de filas, o un objeto con la forma { "records": [...] }.');
}
